import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ACCEPT_RISK, UPDATE_ISSUE_TIME_ESTIMATE } from '../../graphql/mutations';
import { GET_PAGE_RESULT, LIST_RISK_ACCEPTANCES } from '../../graphql/queries';
import {
  CoreWebVitals,
  EaaIssue,
  IssueCategory,
  LighthouseScores,
  PageResult,
  Scan,
  UpdateIssueTimeEstimateResponse,
} from '../../types';
import { truncateUrl } from '../../utils/formatting';
import { AcceptRiskInput, RiskAcceptanceDialog } from '../issues/RiskAcceptanceDialog';
import { PageNavigator } from '../ui/PageNavigator';
import { AccessibilityDetails } from './AccessibilityDetails';
import { AllIssuesTab } from './AllIssuesTab';
import { BrokenLinks } from './BrokenLinks';
import { BusinessImpact } from './BusinessImpact';
import { EaaCompliance } from './EaaCompliance';
import { GeoTab } from './GeoTab';
import { KeyFindings } from './KeyFindings';
import { PageDetailView } from './PageDetailView';
import { PerformanceMetrics } from './PerformanceMetrics';
import { Recommendations } from './Recommendations';
import { SeoTab } from './SeoTab';
import { ToxicBacklinksTab } from './ToxicBacklinksTab';
import { ToxicLinksTab } from './ToxicLinksTab';

const client = generateClient();
const SUMMARY_PAGE_SIZE = 25;

type IssueSeverity = 'critical' | 'serious' | 'moderate' | 'minor';

interface ResultsTabsProps {
  activeTab: string;
  onTabChange: (
    tab:
      | 'summary'
      | 'accessibility'
      | 'eaa'
      | 'seo'
      | 'geo'
      | 'performance'
      | 'links'
      | 'toxic-links'
      | 'toxic-backlinks'
      | 'issues'
      | 'insights'
  ) => void;
  scan: Scan;
  pageResults: PageResult[];
  tabData?: Record<string, PageResult[]>;
  loading?: boolean;
  loadingTabs?: Set<string>;
  initialSeverityFilter?: IssueSeverity;
  gscConnected?: boolean;
  /** Links checked by hand in this scan, and the toggle that records one */
  reviewedLinks?: string[];
  onToggleLinkReviewed?: (targetUrls: string[], reviewed: boolean) => void;
}

const ALL_TABS = [
  { id: 'summary', label: 'Summary', requiresGsc: false },
  { id: 'insights', label: 'Insights', requiresGsc: false },
  { id: 'issues', label: 'All Issues', requiresGsc: false },
  { id: 'accessibility', label: 'Accessibility', requiresGsc: false },
  { id: 'eaa', label: 'EAA Compliance', requiresGsc: false },
  { id: 'seo', label: 'SEO', requiresGsc: false },
  { id: 'geo', label: 'GEO', requiresGsc: false },
  { id: 'performance', label: 'Performance', requiresGsc: false },
  { id: 'links', label: 'Links', requiresGsc: false },
  { id: 'toxic-links', label: 'Toxic Links', requiresGsc: false },
  { id: 'toxic-backlinks', label: 'Toxic Backlinks', requiresGsc: true },
] as const;

export function ResultsTabs({
  activeTab,
  onTabChange,
  scan,
  pageResults,
  tabData,
  loading = false,
  loadingTabs,
  initialSeverityFilter,
  gscConnected = false,
  reviewedLinks = [],
  onToggleLinkReviewed,
}: ResultsTabsProps) {
  const TABS = ALL_TABS.filter((tab) => !tab.requiresGsc || gscConnected);

  // `ScanConfig.scanTypes` is typed with the camelCase values the scan modal works in,
  // but a scan read back from the API carries the GraphQL enum. Accept both rather
  // than trusting either.
  const brokenLinkCheckRan = ((scan.config?.scanTypes ?? []) as string[]).some(
    (type) => type === 'BROKEN_LINKS' || type === 'brokenLinks'
  );
  const { user } = useAuth();

  // State for page drill-down
  const [selectedPageUrl, setSelectedPageUrl] = useState<string | null>(null);

  // Pagination state for Summary tab
  const [summaryPage, setSummaryPage] = useState(1);

  // Risk acceptance dialog state
  const [isRiskDialogOpen, setIsRiskDialogOpen] = useState(false);
  const [selectedIssueForRisk, setSelectedIssueForRisk] = useState<{
    issueId: string;
    issueDescription: string;
    severity?: string;
    category?: string;
    pageUrls?: string[];
  } | null>(null);
  const [acceptedRisks, setAcceptedRisks] = useState<{ issueId: string; expiresAt: string }[]>([]);

  // Load active risk acceptances so reviewed/accepted issues can be excluded from the issue lists.
  useEffect(() => {
    if (!scan.siteId) return;
    (async () => {
      try {
        const res = (await client.graphql({
          query: LIST_RISK_ACCEPTANCES,
          variables: { siteId: scan.siteId, status: 'active' },
        })) as GraphQLResult<{
          listRiskAcceptances: { items: { issueId: string; expiresAt: string }[] };
        }>;
        const items = res.data?.listRiskAcceptances?.items || [];
        setAcceptedRisks(items.map((i) => ({ issueId: i.issueId, expiresAt: i.expiresAt })));
      } catch (e) {
        console.error('Error loading accepted risks:', e);
      }
    })();
  }, [scan.siteId]);

  // Handle opening the risk acceptance dialog
  const handleOpenRiskDialog = (issue: EaaIssue) => {
    setSelectedIssueForRisk({
      issueId: issue.ruleId || issue.clauseNumber,
      issueDescription: issue.requirement || issue.description,
      severity: issue.severity,
      category: issue.category,
      pageUrls: issue.pageUrl ? [issue.pageUrl] : undefined,
    });
    setIsRiskDialogOpen(true);
  };

  // Open the same accept/review dialog for a manual-review issue from the All Issues list.
  const handleMarkReviewed = (r: {
    issueId: string;
    issueDescription: string;
    severity?: string;
    category?: string;
    pageUrls?: string[];
  }) => {
    setSelectedIssueForRisk(r);
    setIsRiskDialogOpen(true);
  };

  // Handle accepting a risk
  const handleAcceptRisk = async (input: AcceptRiskInput) => {
    const result = (await client.graphql({
      query: ACCEPT_RISK,
      variables: { input },
    })) as GraphQLResult<{
      acceptRisk: { acceptanceId: string; issueId: string; expiresAt: string };
    }>;

    if (result.data?.acceptRisk) {
      setAcceptedRisks((prev) => [
        ...prev,
        {
          issueId: result.data!.acceptRisk.issueId,
          expiresAt: result.data!.acceptRisk.expiresAt,
        },
      ]);
    }
  };

  // Handle time estimate update
  const handleTimeEstimateUpdate = async (params: {
    scanId: string;
    pageUrl: string;
    issueType: IssueCategory;
    issueIdentifier: string;
    estimatedTimeToFix: number | null;
  }) => {
    (await client.graphql({
      query: UPDATE_ISSUE_TIME_ESTIMATE,
      variables: {
        input: {
          scanId: params.scanId,
          pageUrl: params.pageUrl,
          issueType: params.issueType,
          issueIdentifier: params.issueIdentifier,
          estimatedTimeToFix: params.estimatedTimeToFix,
        },
      },
    })) as GraphQLResult<{ updateIssueTimeEstimate: UpdateIssueTimeEstimateResponse }>;
  };

  // Calculate paginated results for Summary tab
  const paginatedPageResults = useMemo(() => {
    const startIndex = (summaryPage - 1) * SUMMARY_PAGE_SIZE;
    return pageResults.slice(startIndex, startIndex + SUMMARY_PAGE_SIZE);
  }, [pageResults, summaryPage]);

  const summaryTotalPages = Math.ceil(pageResults.length / SUMMARY_PAGE_SIZE);
  const summaryStartItem = (summaryPage - 1) * SUMMARY_PAGE_SIZE + 1;
  const summaryEndItem = Math.min(summaryPage * SUMMARY_PAGE_SIZE, pageResults.length);

  // Get selected page data via dedicated query so all fields (including brokenLinks) are always present
  const [selectedPage, setSelectedPage] = useState<PageResult | null>(null);

  useEffect(() => {
    if (!selectedPageUrl) {
      setSelectedPage(null);
      return;
    }
    (async () => {
      try {
        const res = (await client.graphql({
          query: GET_PAGE_RESULT,
          variables: { scanId: scan.scanId, pageUrl: selectedPageUrl },
        })) as GraphQLResult<{ getPageResult: PageResult }>;
        setSelectedPage(res.data?.getPageResult ?? null);
      } catch {
        setSelectedPage(null);
      }
    })();
  }, [selectedPageUrl, scan.scanId]);

  // Calculate accessibility score
  const accessibilityScore = useMemo(() => {
    return scan.results?.categoryScores?.accessibility;
  }, [scan]);

  // Extract performance data from page results
  const performanceData = useMemo(() => {
    // Aggregate Core Web Vitals from all pages
    const allVitals: CoreWebVitals[] = pageResults
      .filter((p) => p.coreWebVitals)
      .map((p) => p.coreWebVitals!);

    // Calculate average vitals across all pages
    const aggregatedVitals: CoreWebVitals = {};
    if (allVitals.length > 0) {
      const lcpValues = allVitals.filter((v) => v.lcp !== undefined).map((v) => v.lcp!);
      const fidValues = allVitals.filter((v) => v.fid !== undefined).map((v) => v.fid!);
      const clsValues = allVitals.filter((v) => v.cls !== undefined).map((v) => v.cls!);
      const ttfbValues = allVitals.filter((v) => v.ttfb !== undefined).map((v) => v.ttfb!);

      if (lcpValues.length > 0)
        aggregatedVitals.lcp = lcpValues.reduce((a, b) => a + b, 0) / lcpValues.length;
      if (fidValues.length > 0)
        aggregatedVitals.fid = fidValues.reduce((a, b) => a + b, 0) / fidValues.length;
      if (clsValues.length > 0)
        aggregatedVitals.cls = clsValues.reduce((a, b) => a + b, 0) / clsValues.length;
      if (ttfbValues.length > 0)
        aggregatedVitals.ttfb = ttfbValues.reduce((a, b) => a + b, 0) / ttfbValues.length;
    }

    // Aggregate Lighthouse scores
    const allLighthouse: LighthouseScores[] = pageResults
      .filter((p) => p.lighthouseScores)
      .map((p) => p.lighthouseScores!);

    let aggregatedLighthouse: LighthouseScores | undefined;
    if (allLighthouse.length > 0) {
      // Any of the four scores can be absent (the lighthouse Lambda writes only
      // the categories requested for the scan). Average each independently over
      // the pages that actually have it, so a missing value never yields NaN and
      // a category nobody scanned stays absent.
      const avgField = (key: keyof LighthouseScores): number | undefined => {
        const vals = allLighthouse
          .map((l) => l[key])
          .filter((v): v is number => v !== undefined && v !== null);
        return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : undefined;
      };
      const agg: LighthouseScores = {};
      (['accessibility', 'seo', 'bestPractices', 'performance'] as const).forEach((key) => {
        const v = avgField(key);
        if (v !== undefined) agg[key] = v;
      });
      aggregatedLighthouse = Object.keys(agg).length ? agg : undefined;
    }

    // Get performance score from scan results or lighthouse
    const performanceScore =
      scan.results?.categoryScores?.performance ?? aggregatedLighthouse?.performance;

    return {
      coreWebVitals: aggregatedVitals,
      performanceScore,
      lighthouseScores: aggregatedLighthouse,
    };
  }, [pageResults, scan]);

  // Extract EAA compliance data — use tab-specific data if loaded
  const eaaData = useMemo(() => {
    const source = tabData?.eaa ?? pageResults;
    const allEaaIssues: EaaIssue[] = source
      .filter((p) => p.eaaIssues && p.eaaIssues.length > 0)
      .flatMap((p) => p.eaaIssues!);

    const complianceScore = scan.results?.categoryScores?.eaa;

    return {
      eaaIssues: allEaaIssues.length > 0 ? allEaaIssues : undefined,
      complianceScore,
    };
  }, [tabData?.eaa, pageResults, scan]);
  return (
    <div>
      {/* Tab Navigation - Scrollable on mobile */}
      <div className="relative">
        <div className="flex border-b border-gray-light mb-6 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-4 sm:px-6 py-3 text-body font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0
                ${
                  activeTab === tab.id
                    ? 'border-coral text-coral'
                    : 'border-transparent text-gray-medium hover:text-dark-text'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Fade indicators for scroll on mobile */}
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
      </div>

      {/* Tab Content */}
      {activeTab === 'summary' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-heading-2 font-semibold">Pages Analysed</h2>
            <span className="text-body-sm text-gray-medium">{pageResults.length} pages total</span>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-light">
                <tr>
                  <th className="text-left px-4 py-3 text-body-sm font-semibold">Page URL</th>
                  <th className="text-center px-4 py-3 text-body-sm font-semibold w-24">Issues</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPageResults.map((page, index) => (
                  <tr
                    key={page.pageUrl}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-light/50'}
                  >
                    <td className="px-4 py-3 text-body-sm">
                      <a
                        href={page.pageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-coral hover:underline"
                      >
                        {truncateUrl(page.pageUrl, 60)}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-center text-body-sm">{page.issueCount ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {summaryTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-light bg-white">
                <p className="text-body-sm text-gray-medium">
                  Showing {summaryStartItem} - {summaryEndItem} of {pageResults.length} pages
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSummaryPage((p) => Math.max(1, p - 1))}
                    disabled={summaryPage === 1}
                    className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-body-sm text-gray-medium px-3">
                    Page {summaryPage} of {summaryTotalPages}
                  </span>
                  <button
                    onClick={() => setSummaryPage((p) => Math.min(summaryTotalPages, p + 1))}
                    disabled={summaryPage === summaryTotalPages}
                    className="btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'issues' && (
        <AllIssuesTab
          scanId={scan.scanId}
          initialSeverityFilter={initialSeverityFilter}
          acceptedRisks={acceptedRisks}
          onMarkReviewed={handleMarkReviewed}
          reviewedLinks={reviewedLinks}
          onToggleLinkReviewed={onToggleLinkReviewed}
        />
      )}

      {activeTab === 'insights' && (
        <div className="space-y-10">
          <BusinessImpact
            totalIssues={{
              critical: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.critical || 0,
              serious: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.serious || 0,
              moderate: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.moderate || 0,
              minor: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.minor || 0,
              total: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.total || 0,
            }}
            overallScore={scan.results?.overallScore || 0}
            eaaScore={eaaData.complianceScore}
          />
          <KeyFindings pageResults={pageResults} maxFindings={10} />
          <Recommendations
            pageResults={pageResults}
            totalIssues={{
              critical: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.critical || 0,
              serious: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.serious || 0,
              moderate: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.moderate || 0,
              minor: (scan.results?.uniqueIssues ?? scan.results?.totalIssues)?.minor || 0,
            }}
            categoryScores={scan.results?.categoryScores}
          />
        </div>
      )}

      {activeTab === 'accessibility' &&
        (selectedPage ? (
          <PageDetailView page={selectedPage} onBack={() => setSelectedPageUrl(null)} />
        ) : (
          <div className="space-y-6">
            {pageResults.length > 1 && (
              <PageNavigator
                pages={(tabData?.accessibility ?? pageResults).map((p) => ({
                  url: p.pageUrl,
                  title: p.pageUrl,
                  score: p.score,
                  grade: p.grade,
                  issueCount: p.accessibilityIssues
                    ? new Set(p.accessibilityIssues.map((i) => i.ruleId)).size
                    : p.issueCount,
                }))}
                selectedPageUrl={selectedPageUrl}
                onPageSelect={(url: string | null) => setSelectedPageUrl(url)}
              />
            )}
            <AccessibilityDetails
              pageResults={tabData?.accessibility ?? pageResults}
              accessibilityScore={accessibilityScore}
              loading={loadingTabs?.has('accessibility') ?? loading}
              selectedPageUrl={selectedPageUrl}
            />
          </div>
        ))}

      {activeTab === 'eaa' && (
        <EaaCompliance
          eaaIssues={eaaData.eaaIssues}
          complianceScore={eaaData.complianceScore}
          loading={loadingTabs?.has('eaa') ?? loading}
          onAcceptRisk={handleOpenRiskDialog}
          acceptedRisks={acceptedRisks}
          scanId={scan.scanId}
          onTimeEstimateUpdate={handleTimeEstimateUpdate}
        />
      )}

      {activeTab === 'seo' && (
        <SeoTab
          pageResults={tabData?.seo ?? pageResults}
          loading={loadingTabs?.has('seo') ?? loading}
        />
      )}

      {activeTab === 'geo' && (
        <GeoTab
          pageResults={tabData?.geo ?? pageResults}
          loading={loadingTabs?.has('geo') ?? loading}
        />
      )}

      {activeTab === 'performance' && (
        <PerformanceMetrics
          coreWebVitals={performanceData.coreWebVitals}
          performanceScore={performanceData.performanceScore}
          lighthouseScores={performanceData.lighthouseScores}
          loading={loading}
        />
      )}

      {activeTab === 'links' && (
        <BrokenLinks
          pageResults={tabData?.links ?? pageResults}
          loading={loadingTabs?.has('links') ?? loading}
          selectedPageUrl={selectedPageUrl}
          reviewedLinks={reviewedLinks}
          onToggleLinkReviewed={onToggleLinkReviewed}
        />
      )}

      {activeTab === 'toxic-links' && (
        <ToxicLinksTab
          pageResults={tabData?.['toxic-links'] ?? pageResults}
          loading={loadingTabs?.has('toxic-links') ?? loading}
          reviewedLinks={reviewedLinks}
          onToggleLinkReviewed={onToggleLinkReviewed}
          brokenLinkCheckRan={brokenLinkCheckRan}
        />
      )}

      {activeTab === 'toxic-backlinks' && (
        <ToxicBacklinksTab scanId={scan.scanId} siteId={scan.siteId} gscConnected={gscConnected} />
      )}

      {/* Risk Acceptance Dialog */}
      {selectedIssueForRisk && (
        <RiskAcceptanceDialog
          isOpen={isRiskDialogOpen}
          onClose={() => {
            setIsRiskDialogOpen(false);
            setSelectedIssueForRisk(null);
          }}
          onAccept={handleAcceptRisk}
          issueId={selectedIssueForRisk.issueId}
          issueDescription={selectedIssueForRisk.issueDescription}
          severity={selectedIssueForRisk.severity}
          category={selectedIssueForRisk.category}
          pageUrls={selectedIssueForRisk.pageUrls}
          siteId={scan.siteId}
          userName={user?.email || user?.name || 'Unknown'}
        />
      )}
    </div>
  );
}
